import React, { useEffect, useImperativeHandle, useRef } from 'react';
import { getDurationString } from '../../utils/date';

export interface ElapseTimerRef {
  reset: Function;
  stop: Function;
  getLatestValue: Function;
}

export interface ElapseTimerProps {
  initValue?: number;
  useAbbr?: boolean;
}

export const ElapseTimer = React.forwardRef<ElapseTimerRef, ElapseTimerProps>((
  {
    initValue = 0,
    useAbbr
  },
  ref
) => {
  const startTime = useRef<Date>(null);
  const stopTime = useRef<Date>(null);
  const running = useRef<boolean>(false);
  const timerValueRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    timerValueRef.current.innerHTML = getDurationString(initValue * 1000, useAbbr);
  }, [ initValue ]);

  const reset = () => {
    startTimer();
  };

  const stop = () => {
    stopTimer();
  };

  const getLatestValue = () => {
    if (!stopTime.current || !startTime.current) {
      return;
    }

    return (stopTime.current.getTime() - startTime.current.getTime()) / 1000;
  };

  const startTimer = () => {
    startTime.current = new Date();
    stopTime.current = null;
    running.current = true;

    const tickTimer = () => {
      if (!running.current) {
        return;
      }

      const timeDiffMs = new Date().getTime() - startTime.current.getTime();

      timerValueRef.current.innerHTML = getDurationString(timeDiffMs, useAbbr);

      setTimeout(() => {
        tickTimer();
      }, 200);
    };

    tickTimer();
  };

  const stopTimer = () => {
    running.current = false;

    if (!startTime.current) {
      return;
    }

    stopTime.current = new Date();

    const timeDiffMs = stopTime.current.getTime() - startTime.current.getTime();
    timerValueRef.current.innerHTML = getDurationString(timeDiffMs, useAbbr);
  };

  useImperativeHandle(ref, () => {
    return {
      reset,
      stop,
      getLatestValue
    };
  }, []);

  return (<span ref={timerValueRef} />);
});
